'use client';

import { useEffect, useMemo } from 'react';

import { useNow } from './use-now';

const toEpoch = (date: string | number | undefined | null | Date) => {
  if (typeof date === 'number') return date;
  if (typeof date === 'string') return Date.parse(date);
  if (date instanceof Date) return date.getTime();
  return Number.NaN;
};

const formatNumber = (value: number | null) => {
  return String(value ?? 0);
};

export type DateValue = string | number | undefined | null | Date;

const getDate = (dateValuer: DateValue | (() => DateValue)) => {
  if (typeof dateValuer === 'function') {
    return dateValuer();
  }
  return dateValuer;
};

export function useTimeLeft(
  dateValuer: DateValue | (() => DateValue),
  onReset?: () => void
) {
  const now = useNow();
  const timeLeft = useMemo(() => {
    const endDate = toEpoch(getDate(dateValuer));
    if (Number.isNaN(endDate) || endDate < now) {
      return {
        day: null,
        hours: null,
        minutes: null,
        seconds: null,
        ended: !Number.isNaN(endDate) && endDate < now,
      };
    }
    const remainderDateMiliseconds = endDate - now;

    const weeks =
      Math.floor(remainderDateMiliseconds / 86400000 / 7) > 0
        ? Math.floor(remainderDateMiliseconds / 86400000 / 7)
        : 0;

    const remainderDaysMiliseconds = weeks
      ? remainderDateMiliseconds - weeks * 86400000 * 7
      : remainderDateMiliseconds;

    const days =
      Math.floor(remainderDaysMiliseconds / 86400000) > 0
        ? Math.floor(remainderDaysMiliseconds / 86400000)
        : 0;

    const remainderHoursMiliseconds = days
      ? remainderDaysMiliseconds - days * 86400000
      : remainderDaysMiliseconds;

    const hours =
      Math.floor(remainderHoursMiliseconds / 3600000) > 0
        ? Math.floor(remainderHoursMiliseconds / 3600000)
        : 0;

    const remainderMinutsMiliseconds = hours
      ? remainderHoursMiliseconds - hours * 3600000
      : remainderHoursMiliseconds;

    const minutes =
      Math.floor(remainderMinutsMiliseconds / 60000) > 0
        ? Math.floor(remainderMinutsMiliseconds / 60000)
        : 0;

    const remainderSecondsMilisecond = minutes
      ? remainderMinutsMiliseconds - minutes * 60000
      : remainderMinutsMiliseconds;

    const seconds =
      Math.floor(remainderSecondsMilisecond / 1000) > 0
        ? Math.floor(remainderSecondsMilisecond / 1000)
        : 0;
    return {
      weeks,
      days,
      hours,
      minutes,
      seconds,
      ended: days === 0 && hours === 0 && minutes === 0 && seconds === 0,
    };
  }, [now, dateValuer]);

  useEffect(() => {
    if (timeLeft.ended) {
      onReset?.();
    }
  }, [timeLeft, onReset]);

  const getTimeText = (seperator = ' ', nFragments = 2) => {
    if (timeLeft.ended) return '';

    const fragments: string[] = [];
    const hasWeek = timeLeft.weeks && timeLeft.weeks > 0;
    const hasDay = hasWeek || (timeLeft.days && timeLeft.days > 0);
    const hasHour = hasDay || (timeLeft.hours && timeLeft.hours > 0);
    const hasMinute = hasHour || (timeLeft.minutes && timeLeft.minutes > 0);

    if (hasWeek) {
      fragments.push(`${formatNumber(timeLeft.weeks)}w`);
    }

    if (hasDay) {
      fragments.push(`${formatNumber(timeLeft.days)}d`);
    }

    if (hasHour) {
      fragments.push(`${formatNumber(timeLeft.hours)}h`);
    }

    if (hasMinute) {
      fragments.push(`${formatNumber(timeLeft.minutes)}m`);
    }

    fragments.push(`${formatNumber(timeLeft.seconds)}s`);

    return fragments.slice(0, nFragments).join(seperator);
  };

  return {
    now,
    getTimeText,
    weeks: timeLeft.weeks,
    days: timeLeft.days,
    hours: timeLeft.hours,
    minutes: timeLeft.minutes,
    seconds: timeLeft.seconds,
    ended: timeLeft.ended,
  };
}
